import { FC, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Typography,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Button,
} from "@mui/material";

import ChevronLeftIcon from "../../../icons/ChevronLeftIcon";
import CloseIcon from "../../../icons/CloseIcon";

import Row from "../../core/Row";
import { useChatStore } from "../../../../hooks/useChatStore";
import {
  DEFAULT_SIDEBAR_FILTERS,
  ISidebarFilters,
  SIDEBAR_CHAT_TYPE_OPTIONS,
  SIDEBAR_FILTER_LAST_ACTIVE,
  SIDEBAR_FILTER_LAST_ACTIVE_LABELS,
  SIDEBAR_FILTER_SORT_BY,
  SIDEBAR_FILTER_SORT_BY_LABELS,
} from "../../../../types/sidebar";

const SORT_OPTIONS: { value: SIDEBAR_FILTER_SORT_BY; label: string }[] = [
  SIDEBAR_FILTER_SORT_BY.lastActivity,
  SIDEBAR_FILTER_SORT_BY.dateCreated,
  SIDEBAR_FILTER_SORT_BY.nameAsc,
].map((value) => ({
  value,
  label:
    value === DEFAULT_SIDEBAR_FILTERS.sortBy
      ? `${SIDEBAR_FILTER_SORT_BY_LABELS[value]} (Default)`
      : SIDEBAR_FILTER_SORT_BY_LABELS[value],
}));

const LAST_ACTIVE_OPTIONS: {
  value: SIDEBAR_FILTER_LAST_ACTIVE;
  label: string;
}[] = [
  SIDEBAR_FILTER_LAST_ACTIVE.any,
  SIDEBAR_FILTER_LAST_ACTIVE.last7Days,
  SIDEBAR_FILTER_LAST_ACTIVE.last30Days,
].map((value) => ({
  value,
  label:
    value === DEFAULT_SIDEBAR_FILTERS.lastActive
      ? `${SIDEBAR_FILTER_LAST_ACTIVE_LABELS[value]} (Default)`
      : SIDEBAR_FILTER_LAST_ACTIVE_LABELS[value],
}));

const SECTION_LABEL_SX = {
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.05em",
  color: "text.secondary",
  textTransform: "uppercase" as const,
  mb: 0.5,
};

/** Full-screen "Filter chat history" panel shown in the sidebar in place of
 * the chat list while ChatStore.isSidebarFilterModeEnabled is true. Edits are
 * local (draft) until Apply commits them to ChatStore.sidebarFilters, which
 * Sidebar.tsx reads to filter/sort the chat list — entirely client-side. */
export const SidebarFilterPanel: FC = observer(() => {
  const chatStore = useChatStore();
  const { sidebarFilters } = chatStore;

  const [draft, setDraft] = useState<ISidebarFilters>(sidebarFilters);

  const handleBack = () => {
    chatStore.setIsSidebarFilterModeEnabled(false);
  };

  const handleClear = () => {
    setDraft(DEFAULT_SIDEBAR_FILTERS);
    chatStore.setSidebarFilters(DEFAULT_SIDEBAR_FILTERS);
    chatStore.setIsSidebarFilterModeEnabled(false);
  };

  const handleApply = () => {
    chatStore.setSidebarFilters(draft);
    chatStore.setIsSidebarFilterModeEnabled(false);
  };

  const toggleChatType = (key: string) => {
    setDraft((prev) => ({
      ...prev,
      chatTypes: prev.chatTypes.includes(key)
        ? prev.chatTypes.filter((k) => k !== key)
        : [...prev.chatTypes, key],
    }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        width: "100%",
        overflow: "hidden",
        bgcolor: "white",
      }}
    >
      <Row
        sx={{
          height: "auto",
          alignItems: "center",
          gap: 1,
          py: 2,
          px: 2,
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          flexShrink: 0,
        }}
      >
        <IconButton size="small" onClick={handleBack} aria-label="Back">
          <ChevronLeftIcon />
        </IconButton>
        <Typography fontWeight={700} fontSize="1rem">
          Filter chat history
        </Typography>
      </Row>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2, py: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography sx={SECTION_LABEL_SX}>Sort by</Typography>
          <RadioGroup
            value={draft.sortBy}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                sortBy: e.target.value as SIDEBAR_FILTER_SORT_BY,
              }))
            }
          >
            {SORT_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio size="small" />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography sx={SECTION_LABEL_SX}>Chat type</Typography>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {SIDEBAR_CHAT_TYPE_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.key}
                control={
                  <Checkbox
                    size="small"
                    checked={draft.chatTypes.includes(option.key)}
                    onChange={() => toggleChatType(option.key)}
                  />
                }
                label={option.label}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography sx={SECTION_LABEL_SX}>Last active</Typography>
          <RadioGroup
            value={draft.lastActive}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                lastActive: e.target.value as SIDEBAR_FILTER_LAST_ACTIVE,
              }))
            }
          >
            {LAST_ACTIVE_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio size="small" />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </Box>

        <Box>
          <Typography sx={SECTION_LABEL_SX}>Status</Typography>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={draft.hideClosedChats}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    hideClosedChats: e.target.checked,
                  }))
                }
              />
            }
            label="Hide closed chats"
          />
        </Box>
      </Box>

      <Row
        sx={{
          height: "auto",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 2,
          borderTop: "1px solid rgba(0, 0, 0, 0.12)",
          flexShrink: 0,
        }}
      >
        <Button
          variant="text"
          startIcon={<CloseIcon size={16} />}
          onClick={handleClear}
          sx={{ color: "error.dark" }}
        >
          Clear Filters
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleApply}
          sx={{ borderRadius: "8px", px: 3 }}
        >
          Apply
        </Button>
      </Row>
    </Box>
  );
});

export default SidebarFilterPanel;
